import type { MetadataReview, MetadataSuggestion } from './recipeMetadata';
import type { RecipeTimeline, TimelineStatus } from '../utils/recipeTimeline';
import type { TimeSource } from '../utils/recipeTimings';

// Recipe types with flexible format supporting imported, Google, and personal recipes

export interface RecipeIngredient {
  id: number;
  name: string;
  original: string; // Original text from recipe
  amount: number;
  unit: string;
  image?: string;
}

export interface RecipeNutrition {
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
}

/**
 * One step's ingredients, resolved once when the recipe is imported.
 *
 * Keyed by the step's text rather than its position: the screens feed chips
 * from several instruction shapes (raw, merged, sectioned) whose indices do not
 * line up, and a recipe re-scrape can renumber steps. Text is stable across all
 * of them, and a step whose text is not in the map simply falls back to the
 * string matcher, so a partly-stale map still helps rather than misleads.
 */
export interface StepIngredientMap {
  /** The step exactly as stored in `instructions`. */
  step: string;
  /** Indices into `extendedIngredients`. */
  ingredients: number[];
}

export interface RecipeInstruction {
  number: number;
  step: string;
}

export interface RecipeInstructionSection {
  name?: string;  // Section header (e.g., "Aubergines", "Sauce tomate")
  steps: RecipeInstruction[];
}

export interface RecipeIngredientGroup {
  name: string;  // Section header (e.g., "Vinaigrette", "Salade")
  items: string[];  // Ingredient strings belonging to this group
}

/**
 * Lifecycle of a recipe imported as part of a collection batch:
 * pendingReview → approved | rejected, and approved → published once it has
 * been promoted into the shared public /recipes catalog.
 */
export type RecipeReviewStatus =
  | 'pendingReview'
  | 'approved'
  | 'rejected'
  | 'published';

export interface Recipe {
  id: number | string; // Support both numeric (Spoonacular) and string (Personal) IDs
  title: string;
  image?: string;
  imageType?: string;
  servings: number;
  servingsDisplay?: string; // Display text for servings (e.g., "4 to 6" instead of just 4)
  readyInMinutes: number;
  cookingMinutes?: number;
  preparationMinutes?: number;
  /** « Préparation »: minutes the cook's hands are busy, derived from `timeline`. */
  handsOnMinutes?: number;
  /** Shortest « Prêt en » when the timeline's waits are ranges; `readyInMinutes` is the longest. */
  readyInMinutesMin?: number;
  /** The total the publisher printed, kept when the model's `readyInMinutes` replaces it. */
  statedReadyInMinutes?: number;
  timeline?: RecipeTimeline;
  timelineStatus?: TimelineStatus;
  /** Who stated the time. Written by `updateRecipe` when a cook edits one; nothing on the client reads it. */
  timeSource?: TimeSource;
  aggregateLikes?: number;
  healthScore?: number;
  spoonacularScore?: number;
  pricePerServing?: number;
  cheap?: boolean;
  creditsText?: string;
  license?: string;
  sourceName?: string;
  sourceUrl?: string;
  /**
   * The address this recipe was originally saved under, kept when `sourceUrl`
   * was repaired. Populated only for recipes that arrived via a newsletter
   * click-tracking wrapper, so the original link stays auditable after the
   * rewrite. See #575.
   */
  originalSourceUrl?: string;
  weightWatcherSmartPoints?: number;
  gaps?: string;
  lowFodmap?: boolean;
  sustainable?: boolean;
  ketogenic?: boolean;
  whole30?: boolean;
  sourceRecipeUrl?: string;
  summary?: string;
  cuisines?: string[];
  dishTypes?: string[];
  diets?: string[];
  occasions?: string[];
  winePairing?: {
    pairedWines: string[];
    pairingText: string;
    productMatches: any[];
  };
  instructions?: RecipeInstruction[];
  instructionSections?: RecipeInstructionSection[];  // Sectioned instructions with headers
  ingredients?: string[]; // Simple ingredient list (used by imported/personal recipes)
  extendedIngredients?: RecipeIngredient[]; // Detailed ingredients (used by Spoonacular API)
  stepIngredients?: StepIngredientMap[];    // Which ingredients each step uses, resolved once at import
  ingredientGroups?: RecipeIngredientGroup[]; // Sectioned ingredients (e.g., "Vinaigrette", "Salade")
  nutrition?: RecipeNutrition;

  // Custom fields for our suggestion system
  ingredientMatchScore?: number; // 0-100 percentage of ingredients available
  expiringSoonIngredients?: string[]; // Names of expiring ingredients used
  missingIngredients?: RecipeIngredient[] | string[]; // Ingredients not in inventory (detailed or simple format)
  availableIngredients?: RecipeIngredient[] | string[]; // Ingredients we have in inventory (detailed or simple format)
  suggestionPriority?: number; // Higher = more urgent (based on expiring items)

  // Personal recipe identification
  isPersonalRecipe?: boolean; // True if this is from RecipeKeeper export
  personalRecipeId?: string; // Original RecipeKeeper UUID

  // Language support
  originalLanguage?: 'en' | 'fr' | 'unknown'; // Detected original language
  translatedTitles?: { en?: string; fr?: string };
  availableLanguages?: Array<'en' | 'fr'>; // Languages this recipe is available in
  translatedVersions?: { [key in 'en' | 'fr']?: Recipe }; // Cached translated versions
  isTranslated?: boolean; // True if this is a translated version
  translatedFrom?: 'en' | 'fr'; // Original language if this is translated
  /**
   * True only when the INGREDIENTS were translated, not just the title.
   *
   * `isTranslated` cannot answer this. The meal-plan path falls back to a
   * title-only translation whenever no full translation is stored, and that
   * fallback sets `isTranslated: true` while `extendedIngredients` stay in the
   * source language — which is how a French app shipped English shopping-list
   * rows with no error and no log (#626).
   *
   * Written in exactly one place: `applyStoredTranslation`. The title-only
   * fallback must never set it. It also feeds `generateWeekSnapshot`, so the
   * grocery list rebuilds when a full translation lands on top of a title-only
   * one — without it the titles match, the hash matches, and the stale
   * source-language list is served as fresh.
   */
  ingredientsTranslated?: boolean;

  // Source tracking
  _perplexitySource?: boolean; // True if this recipe came from Perplexity API
  _imported?: boolean; // True if this recipe came from browser extension import
  _googleSource?: boolean; // True if this recipe came from Google Custom Search
  _importedAt?: string; // Timestamp when recipe was imported (ISO string)
  _firestoreId?: string; // Original Firestore document ID (for imported recipes with hashed IDs)

  // Source website rating
  sourceRating?: number;
  sourceRatingCount?: number;
  sourceRatingScale?: number;

  // Scraping tracking
  lastScrapedAt?: string; // Timestamp when recipe was last scraped for enrichment (ISO string)
  enrichmentAttemptCount?: number; // Number of background metadata-enrichment attempts made (success or failure) — caps retries instead of trying forever or only once

  // Collection-import review workflow. Only set on recipes saved as part of a
  // batch (a whole roundup page saved at once); a normal one-off save leaves
  // these undefined and behaves exactly as before.
  reviewStatus?: RecipeReviewStatus;
  importBatchId?: string; // Groups every recipe saved from one roundup page
  importBatchTitle?: string; // Headline of the roundup, e.g. "Our Best Crunchy Recipes"
  importBatchSourceUrl?: string; // The roundup URL itself, not the recipe's sourceUrl
  importedVia?: string; // e.g. 'chrome-extension-batch'
  publishedAt?: string; // Set when promoted into the shared /recipes catalog (ISO string)
  // Import-review tagging loop (admin). The model's suggestion is stored apart
  // from the real tags; a reviewer's accept or correction writes the tags and
  // stamps metadataReview. See api/_lib/recipeMetadataReview.ts.
  metadataSuggestion?: MetadataSuggestion;
  metadataReview?: MetadataReview;

  // Video recipe fields
  videoUrl?: string; // Original video URL (Instagram, Facebook, TikTok, YouTube)
  videoPlatform?: 'instagram' | 'facebook' | 'youtube' | 'tiktok'; // Video platform
  videoThumbnail?: string; // Video thumbnail image URL
}

// Protein categories for meal plan diversity
export enum ProteinCategory {
  POULTRY = 'poultry',
  RED_MEAT = 'red_meat',
  SEAFOOD = 'seafood',
  PLANT = 'plant',
  DAIRY_EGGS = 'dairy_eggs',
  VEGETARIAN = 'vegetarian',
}

export interface RecipeSuggestion {
  recipe: Recipe;
  matchScore: number; // Overall match score (0-100)
  expiringSoonCount: number; // Number of expiring ingredients this recipe uses
  availableCount: number; // Number of ingredients we have
  totalCount: number; // Total ingredients in recipe
  urgencyScore: number; // Priority based on expiring items
  reasonText: string; // Human-readable reason for suggestion
  proteinCategory?: ProteinCategory; // Primary protein type in this recipe
  proteinIngredients?: string[]; // Specific protein ingredients (e.g., ["chicken", "eggs"])
}

export interface SearchMetadata {
  ingredientsUsed: string[]; // Which ingredients from inventory were selected
  expiringIngredientsUsed: string[]; // Which of those are expiring soon
  sourcesSearched: string[]; // Which websites were queried (domains or display names)
  queriesGenerated: string[]; // Actual search strings sent to Google
}

// Smart Meal Plan types
export interface PlannedMealSuggestion {
  day: string; // "Monday", "Tuesday", etc.
  dayOfWeek: number; // 0-6 (Sunday = 0)
  assignedDate: string; // ISO date for this week (YYYY-MM-DD)
  suggestion: RecipeSuggestion;
}

export interface SmartMealPlan {
  meals: PlannedMealSuggestion[];
  proteinDiversity: Record<ProteinCategory, number>;
  expiringIngredientsUsed: number;
  totalExpiringIngredients: number;
}

export interface RecipeSuggestionsResponse {
  suggestions: RecipeSuggestion[];
  totalInventoryItems: number;
  expiringSoonItems: number;
  suggestedRecipesCount: number;
  lastUpdated: string;
  searchMetadata?: SearchMetadata; // Information about how the search was performed
  smartMealPlan?: SmartMealPlan; // Curated 5-meal weekly plan with protein diversity
}

// Diet and cuisine filters
export type DietType = 
  | 'vegetarian' 
  | 'vegan' 
  | 'gluten-free' 
  | 'dairy-free' 
  | 'keto' 
  | 'paleo' 
  | 'low-carb' 
  | 'mediterranean';

export type CuisineType = 
  | 'american' 
  | 'italian' 
  | 'mexican' 
  | 'asian' 
  | 'mediterranean' 
  | 'french' 
  | 'indian' 
  | 'chinese' 
  | 'thai' 
  | 'japanese';

export type MealType = 
  | 'breakfast' 
  | 'lunch' 
  | 'dinner' 
  | 'snack' 
  | 'dessert';

export interface RecipeFilters {
  diets: DietType[];
  cuisines: CuisineType[];
  mealTypes: MealType[];
  maxReadyTime?: number; // in minutes
  minIngredientMatch?: number; // percentage 0-100
  prioritizeExpiring: boolean;
  requireProtein?: boolean; // Ensure recipes contain protein sources
  prioritizePersonalRecipes?: boolean; // Prefer personal recipes over API results
}

// Helper functions for protein categorization
export function getProteinCategory(recipe: Recipe): ProteinCategory {
  const ingredients = recipe.extendedIngredients || [];
  const ingredientNames = ingredients.map(ing => ing?.name?.toLowerCase() || '').join(' ');
  // Safety: filter out non-string values before calling toLowerCase()
  const dishTypes = (recipe.dishTypes || [])
    .filter(dt => typeof dt === 'string')
    .map(dt => dt.toLowerCase())
    .join(' ');

  // Define protein keywords for each category
  const poultryKeywords = ['chicken', 'turkey', 'duck', 'poultry', 'hen'];
  const redMeatKeywords = ['beef', 'pork', 'lamb', 'veal', 'bison', 'bacon', 'ham', 'sausage', 'steak', 'ground beef'];
  const seafoodKeywords = ['fish', 'salmon', 'tuna', 'cod', 'tilapia', 'shrimp', 'crab', 'lobster', 'scallop', 'mussel', 'oyster', 'sardine', 'anchovy', 'prawn', 'seafood'];
  const plantKeywords = ['beans', 'lentils', 'chickpea', 'tofu', 'tempeh', 'seitan', 'edamame', 'quinoa'];
  const dairyEggKeywords = ['egg', 'eggs', 'cheese', 'yogurt', 'greek yogurt', 'cottage cheese'];

  // Check in order of specificity
  if (poultryKeywords.some(kw => ingredientNames.includes(kw))) {
    return ProteinCategory.POULTRY;
  }
  if (seafoodKeywords.some(kw => ingredientNames.includes(kw))) {
    return ProteinCategory.SEAFOOD;
  }
  if (redMeatKeywords.some(kw => ingredientNames.includes(kw))) {
    return ProteinCategory.RED_MEAT;
  }
  if (plantKeywords.some(kw => ingredientNames.includes(kw))) {
    return ProteinCategory.PLANT;
  }
  if (dairyEggKeywords.some(kw => ingredientNames.includes(kw))) {
    return ProteinCategory.DAIRY_EGGS;
  }

  // Check dish types for vegetarian indicators
  const vegetarianDishes = ['salad', 'soup', 'pasta', 'side dish', 'appetizer', 'snack'];
  if (vegetarianDishes.some(type => dishTypes.includes(type))) {
    return ProteinCategory.VEGETARIAN;
  }

  return ProteinCategory.VEGETARIAN;
}

export function getProteinIngredients(recipe: Recipe): string[] {
  const ingredients = recipe.extendedIngredients || [];
  const allProteinKeywords = [
    'chicken', 'turkey', 'duck', 'beef', 'pork', 'lamb', 'bacon', 'ham', 'sausage',
    'fish', 'salmon', 'tuna', 'cod', 'shrimp', 'crab', 'lobster',
    'eggs', 'egg', 'cheese', 'yogurt', 'tofu', 'beans', 'lentils', 'chickpea', 'tempeh'
  ];

  const proteinIngredients: string[] = [];
  for (const ing of ingredients) {
    if (!ing || !ing.name) continue;
    const name = ing.name.toLowerCase();
    for (const keyword of allProteinKeywords) {
      if (name.includes(keyword)) {
        proteinIngredients.push(ing.name);
        break;
      }
    }
  }

  return proteinIngredients;
}

export function getProteinIcon(category: ProteinCategory): string {
  switch (category) {
    case ProteinCategory.POULTRY:
      return '🐔';
    case ProteinCategory.RED_MEAT:
      return '🥩';
    case ProteinCategory.SEAFOOD:
      return '🐟';
    case ProteinCategory.PLANT:
      return '🌱';
    case ProteinCategory.DAIRY_EGGS:
      return '🥚';
    case ProteinCategory.VEGETARIAN:
      return '🥕';
    default:
      return '🍽️';
  }
}